import { useEffect, useRef, useState } from 'react';
import { createRoot } from 'react-dom/client';
import './styles.css';

const APP_ORIGIN = String(import.meta.env.VITE_INEDITA_APP_ORIGIN || 'https://inedito.schlapp.com.br').replace(/\/$/, '');
const visualRoot = '/visuals/biomorphic';
const journeyVideoSrc = String(import.meta.env.VITE_INEDITA_JOURNEY_VIDEO_SRC || '').trim();
const journeyPosterSrc = String(import.meta.env.VITE_INEDITA_JOURNEY_POSTER_SRC || '').trim();

const image = (name) => `${visualRoot}/${name}.webp`;
const appPath = (path) => `${APP_ORIGIN}${path}`;
const startHref = appPath('/entrar?next=%2Fapp');
const checkoutHref = appPath('/entrar?next=%2Fcheckout');
const applyHref = appPath('/entrar?next=%2Fapoio');

const bioAssets = {
  hero: { src: image('BIOMORPHIC-01-novo-gargalo'), alt: 'Estrutura biomórfica transparente envolve um núcleo luminoso entre formas escuras.' },
  vocabulary: { src: image('BIOMORPHIC-03-mergulho'), alt: 'Camadas translúcidas e linhas de luz se aprofundam em uma estrutura escura, evocando uma investigação em andamento.' },
  method: { src: image('BIOMORPHIC-04-cartografia'), alt: 'Fragmentos luminosos se conectam por linhas finas até formar uma configuração única sobre um campo escuro.' },
  questions: { src: image('BIOMORPHIC-02-perguntas'), alt: 'Instrumentos ópticos luminosos aparecem dentro de uma forma biomórfica escura, sugerindo investigação cuidadosa.' },
  extraction: { src: image('BIOMORPHIC-05-extracao'), alt: 'Um núcleo verde-azulado se destaca dentro de uma estrutura orgânica escura, como um sinal extraído de muitos fragmentos.' },
  council: { src: image('BIOMORPHIC-06-conselho'), alt: 'Lentes e estruturas circulares examinam um núcleo luminoso sob diferentes ângulos em um ambiente escuro.' },
  reality: { src: image('BIOMORPHIC-07-realidade'), alt: 'Um núcleo luminoso atravessa formas de pressão e equilíbrio, sugerindo uma hipótese confrontada com a realidade.' },
  reveal: { src: image('BIOMORPHIC-08-revelacao'), alt: 'Uma forma luminosa é revelada dentro de uma estrutura biomórfica, sugerindo uma combinação pessoal compreendida.' },
  execution: { src: image('BIOMORPHIC-09-execucao'), alt: 'Um núcleo central se desdobra em caminhos organizados dentro de uma estrutura escura, sugerindo opções de execução.' },
  human: { src: image('BIOMORPHIC-10-estrategia-humana'), alt: 'Uma passagem entre instrumento de precisão e dossiê humano representa a transição para uma decisão acompanhada.' },
};

const creationWords = ['sites', 'aplicativos', 'vídeos', 'marcas', 'automações', 'conteúdo', 'negócios'];
const HERO_TAIL = 'saber o que vale a pena criar.';

const publicOffers = [
  { price: 'R$297', name: 'INÉDITA - DESCOBERTA', copy: 'Descubra o que você poderia criar.', cta: 'Descobrir minhas oportunidades' },
  { price: 'R$997', name: 'INÉDITA - EXECUÇÃO', copy: 'Não descubra apenas o que fazer. Descubra como fazer.', cta: 'Quero a ideia + plano' },
  { price: 'R$1.497', name: 'INÉDITA - ESTRATÉGIA', copy: 'Chegue à conversa com a investigação e o plano já estruturados.', cta: 'Quero o Método + sessão estratégica' },
];

function BrandLockup({ href = '/', className = '' }) {
  return <a className={`lab-brand ${className}`.trim()} href={href} aria-label="INÉDITA Labs, página inicial"><span className="lab-brand__mark" aria-hidden="true"><img src="/brand/inedito-gota-semente-alpha.webp" alt="" /></span><span>INÉDITA <small>LABS</small></span></a>;
}

function Header({ variant = 'dark', label = 'Landing Lab', publicSurface = false, brandHref = publicSurface ? '/biomorphic' : '/' }) {
  return (
    <header className={`lab-header lab-header--${variant} ${publicSurface ? 'lab-header--public' : ''}`}>
      <BrandLockup href={brandHref} />
      {!publicSurface ? <span className="lab-header__label">{label}</span> : null}
      {!publicSurface ? <nav className="lab-nav" aria-label="Variações da landing page"><a href="/biomorphic">Biomorphic</a><a href="/business">Empresas</a><a href="/journey">Jornada</a></nav> : null}
      <a className="text-link" href={startHref}>Começar</a>
    </header>
  );
}

function Arrow() {
  return <span className="arrow" aria-hidden="true"><svg viewBox="0 0 16 16" focusable="false"><path d="M3 13 13 3M5 3h8v8" /></svg></span>;
}

function CTA({ href, children, tone = 'dark' }) {
  return <a className={`cta cta--${tone}`} href={href}>{children}<Arrow /></a>;
}

function Figure({ src, alt, className = '', priority = false }) {
  return <figure className={`image-figure ${className}`.trim()}><img src={src} alt={alt} loading={priority ? 'eager' : 'lazy'} fetchPriority={priority ? 'high' : 'auto'} decoding="async" /><span aria-hidden="true" /></figure>;
}

function Footer({ variant = 'dark' }) {
  return <footer className={`lab-footer lab-footer--${variant}`}><BrandLockup /><span>Observar. Decidir. Agir. Medir. Aprender. Adaptar.</span><a href={applyHref}>Acompanhamento por aplicação <Arrow /></a></footer>;
}

function useReducedMotionPreference() {
  const [reducedMotion, setReducedMotion] = useState(() => typeof window !== 'undefined' && window.matchMedia('(prefers-reduced-motion: reduce)').matches);
  useEffect(() => {
    const media = window.matchMedia('(prefers-reduced-motion: reduce)');
    const change = (event) => setReducedMotion(event.matches);
    media.addEventListener?.('change', change);
    return () => media.removeEventListener?.('change', change);
  }, []);
  return reducedMotion;
}

function useHeroTail(reducedMotion) {
  const [tail, setTail] = useState(HERO_TAIL);
  useEffect(() => {
    if (reducedMotion) {
      setTail(HERO_TAIL);
      return undefined;
    }
    const alphabet = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
    let interval = 0;
    const reveal = (progress) => {
      const locked = Math.floor(progress * HERO_TAIL.length);
      setTail(HERO_TAIL.split('').map((character, index) => {
        if (character === ' ' || index < locked) return character;
        return alphabet[(index * 13 + Math.floor(progress * 120)) % alphabet.length];
      }).join(''));
    };
    const start = window.setTimeout(() => {
      let step = 0;
      interval = window.setInterval(() => {
        step += 1;
        const progress = Math.min(1, step / 16);
        reveal(progress);
        if (progress === 1) {
          window.clearInterval(interval);
          setTail(HERO_TAIL);
        }
      }, 46);
    }, 260);
    const failSafe = window.setTimeout(() => {
      if (interval) window.clearInterval(interval);
      setTail(HERO_TAIL);
    }, 1450);
    return () => { window.clearTimeout(start); window.clearTimeout(failSafe); if (interval) window.clearInterval(interval); };
  }, [reducedMotion]);
  return tail;
}

function useBiomorphicStageMotion(rootRef, reducedMotion) {
  useEffect(() => {
    const root = rootRef.current;
    if (!root) return undefined;
    const stages = [...root.querySelectorAll('[data-bio-stage]')];
    if (reducedMotion || !('IntersectionObserver' in window)) {
      stages.forEach((stage) => stage.classList.add('is-visible'));
      return undefined;
    }
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => { if (entry.isIntersecting) entry.target.classList.add('is-visible'); });
    }, { threshold: 0.18 });
    stages.forEach((stage) => observer.observe(stage));
    return () => observer.disconnect();
  }, [rootRef, reducedMotion]);
}

function useCreationVocabulary(reducedMotion) {
  const ref = useRef(null);
  useEffect(() => {
    const node = ref.current;
    if (!node) return undefined;
    const words = [...node.querySelectorAll('[data-vocabulary-word]')];
    if (reducedMotion) {
      node.classList.remove('is-scroll-controlled');
      words.forEach((word) => word.classList.add('is-active'));
      return undefined;
    }
    let frame = 0;
    const update = () => {
      frame = 0;
      const stage = node.closest('[data-bio-stage]') || node;
      const rect = stage.getBoundingClientRect();
      const travel = Math.max(1, stage.offsetHeight - window.innerHeight);
      const progress = Math.min(1, Math.max(0, -rect.top / travel));
      const isInReadingRange = rect.top < window.innerHeight && rect.bottom > 0;
      const activeCount = isInReadingRange ? Math.max(1, Math.ceil(progress * creationWords.length)) : 0;
      words.forEach((word, index) => {
        word.classList.toggle('is-active', index < activeCount);
        word.classList.toggle('is-current', index === activeCount - 1);
        word.classList.toggle('is-pending', index >= activeCount);
      });
      node.classList.add('is-scroll-controlled');
    };
    const schedule = () => {
      if (frame) window.cancelAnimationFrame(frame);
      update();
      frame = window.requestAnimationFrame(update);
    };
    schedule();
    window.addEventListener('scroll', schedule, { passive: true });
    window.addEventListener('resize', schedule);
    return () => { window.removeEventListener('scroll', schedule); window.removeEventListener('resize', schedule); if (frame) window.cancelAnimationFrame(frame); };
  }, [reducedMotion]);
  return ref;
}

function StageBackdrop({ visual, priority = false }) {
  return <div className="bio-stage__scene" aria-hidden="true"><img src={visual.src} alt="" loading={priority ? 'eager' : 'lazy'} fetchPriority={priority ? 'high' : 'auto'} decoding="async" /><span className="bio-stage__veil" /><svg className="bio-stage__threads" viewBox="0 0 1000 700" preserveAspectRatio="none"><path d="M-20 562 C185 428 304 616 488 418 S761 238 1020 160" /><path d="M-20 630 C192 488 345 662 576 520 S803 338 1020 292" /></svg><span className="bio-stage__signal" /></div>;
}

function BioStage({ id, visual, className = '', priority = false, children }) {
  return <section id={id} className={`bio-stage ${className}`.trim()} data-bio-stage><StageBackdrop visual={visual} priority={priority} /><div className="bio-stage__content">{children}</div></section>;
}

function PublicLadder({ tone = 'dark', visual = null }) {
  return (
    <section className={`offer-ladder offer-ladder--${tone} ${visual ? 'offer-ladder--visual' : ''}`} aria-labelledby="offer-title" data-bio-stage={visual ? '' : undefined}>
      {visual ? <StageBackdrop visual={visual} /> : null}
      <div className="section-lead"><p className="section-kicker">Escolha a profundidade</p><h2 id="offer-title">A descoberta pode virar caminho.</h2><p>Os três caminhos públicos aumentam a profundidade da decisão. O acompanhamento mais próximo acontece por aplicação.</p></div>
      <div className="offer-list">
        {publicOffers.map((offer, index) => <article className={`offer-row ${index === 1 ? 'offer-row--active' : ''}`} key={offer.price}>
          <span className="offer-row__index">0{index + 1}</span><div><h3>{offer.name}</h3><p>{offer.copy}</p></div><strong>{offer.price}</strong><a href={checkoutHref}>{offer.cta}<Arrow /></a>
        </article>)}
      </div>
    </section>
  );
}

function BiomorphicPage() {
  const pageRef = useRef(null);
  const reducedMotion = useReducedMotionPreference();
  const heroTail = useHeroTail(reducedMotion);
  const vocabularyRef = useCreationVocabulary(reducedMotion);
  useBiomorphicStageMotion(pageRef, reducedMotion);
  return <main ref={pageRef} className="page page--biomorphic">
    <Header variant="dark" publicSurface />
    <BioStage id="biomorphic-hero" className="bio-hero" visual={bioAssets.hero} priority><div className="bio-hero__copy"><h1 aria-label={`Se todos podem criar praticamente qualquer coisa, o verdadeiro gargalo passa a ser ${HERO_TAIL}`}><span>Se todos podem criar praticamente qualquer coisa,</span><span>o verdadeiro gargalo passa a ser</span><strong aria-hidden="true">{heroTail}</strong></h1><p>Não falta ferramenta. Falta reconhecer o que merece existir antes de gastar energia construindo.</p><a className="bio-scroll-cue" href="#novo-gargalo">Role para investigar <Arrow /></a></div></BioStage>

    <BioStage id="novo-gargalo" className="bio-vocabulary-stage" visual={bioAssets.vocabulary}><div className="bio-vocabulary-stage__layout"><div><p className="bio-statement">Em um mundo onde praticamente qualquer pessoa pode usar IA para criar...</p><div ref={vocabularyRef} className="bio-vocabulary" aria-label={creationWords.join(', ')}>{creationWords.map((word) => <span className="is-active" data-vocabulary-word key={word}>{word}</span>)}</div></div><div className="bio-turn"><p>Se criar deixou de ser o problema...</p><h2>onde está o novo gargalo?</h2><strong>Na decisão sobre o que merece existir.</strong></div></div></BioStage>

    <BioStage id="metodo" className="bio-method" visual={bioAssets.method}><div className="bio-copy"><p className="bio-label">INÉDITA LABS APRESENTA</p><h2>A oportunidade que só faz sentido para você.</h2><p>Existe uma oportunidade escondida na combinação do que você sabe, viveu, possui e consegue acessar.</p><strong>O Método INÉDITA foi criado para encontrá-la.</strong><CTA href={startHref}>Descobrir minhas oportunidades</CTA></div></BioStage>

    <BioStage id="investigacao" className="bio-questions" visual={bioAssets.questions}><div className="bio-copy"><h2>Talvez você não precise de melhores respostas.</h2><p className="bio-questions__answer">Talvez precise de perguntas que nunca fizeram para você.</p><p>Uma investigação premium acompanha o sinal e aumenta a resolução exatamente onde ainda falta evidência.</p><a className="inline-link inline-link--light" href={startHref}>Começar minha investigação <Arrow /></a></div></BioStage>

    <BioStage id="extracao" className="bio-extraction" visual={bioAssets.extraction}><div className="bio-copy"><h2>O que parecia comum começa a revelar valor.</h2><p>Conhecimento subestimado, problemas monetizáveis, acessos pouco explorados e restrições reais podem formar uma combinação difícil de reproduzir em outra pessoa.</p><blockquote>Não procuramos apenas aquilo em que você é bom. Procuramos combinações que sejam difíceis de reproduzir.</blockquote></div></BioStage>

    <BioStage id="conselho" className="bio-council" visual={bioAssets.council}><div className="bio-copy"><h2>Uma ideia não chega até você só porque parece boa.</h2><p>Antes de aparecer, uma possibilidade atravessa olhares que confrontam potencial, aderência, fragilidade, modelo de negócio e realidade.</p><strong>Antes de chegar até você, sua ideia precisa sobreviver ao Conselho.</strong></div></BioStage>

    <BioStage id="realidade" className="bio-reality" visual={bioAssets.reality}><div className="bio-copy"><h2>Porque uma ideia fascinante ainda pode ser um péssimo negócio.</h2><p>Quem pagaria? Por que agora? Como a pessoa seria alcançada? É possível validar antes de construir tudo?</p><strong>Ideias interessantes não são automaticamente oportunidades que merecem ser exploradas.</strong></div></BioStage>

    <BioStage id="revelacao" className="bio-reveal" visual={bioAssets.reveal}><div className="bio-copy"><p className="bio-label">A REVELAÇÃO</p><h2>Encontramos uma assimetria.</h2><p>Você entende por que aquela possibilidade apareceu especificamente para você, o que a sustenta e o que ainda precisa ser provado.</p><CTA href={startHref}>Descobrir minhas oportunidades</CTA></div></BioStage>

    <PublicLadder tone="dark" visual={bioAssets.execution} />
    <BioStage id="acompanhamento" className="bio-application" visual={bioAssets.human}><div className="bio-copy"><h2>Algumas ideias pedem mais do que um plano.</h2><p>Para determinados projetos, a INÉDITA Labs oferece formatos de acompanhamento mais próximos, sujeitos à análise do projeto e disponibilidade.</p></div><CTA href={applyHref} tone="light">Aplicar para acompanhamento</CTA></BioStage>
    <Footer variant="dark" />
  </main>;
}

const seededSignal = (index, offset = 0) => {
  const value = Math.sin((index + 1) * 12.9898 + (offset + 1) * 78.233) * 43758.5453;
  return value - Math.floor(value);
};

const constrain = (value, min = 0, max = 1) => Math.min(max, Math.max(min, value));
const soften = (value, min = 0, max = 1) => {
  const t = constrain((value - min) / Math.max(.0001, max - min));
  return t * t * (3 - 2 * t);
};

const cinemaActs = [
  {
    point: 0,
    title: <>Uma oportunidade pode já estar <em>na sua empresa.</em></>,
    copy: 'Capacidades, ativos, acessos e problemas reais deixam pistas. Antes de investir, vale enxergar o que essas pistas podem estar dizendo juntas.',
  },
  {
    point: .29,
    title: <>A empresa já produz sinais que ainda <em>não se encontram.</em></>,
    copy: 'Eles aparecem em reuniões, rotinas, conversas com clientes e decisões aparentemente isoladas.',
  },
  {
    point: .58,
    title: <>Quando relações aparecem, uma hipótese deixa de ser <em>palpite.</em></>,
    copy: 'O Método INÉDITA aproxima o que estava disperso e mostra o que merece ser compreendido antes de avançar.',
  },
  {
    point: .86,
    title: <>A próxima decisão pode começar com <em>uma direção.</em></>,
    copy: 'Sem promessas automáticas. Com mais clareza sobre a realidade que sustenta o próximo experimento.',
    cta: 'Mapear uma oportunidade para minha empresa',
  },
];

const MORPH_PARTICLE_COUNT = 2400;
const particlePalette = ['235, 231, 219', '197, 168, 115', '134, 179, 155', '92, 142, 132', '150, 112, 136'];
const cinemaForms = ['cloud', 'orbit', 'bulb', 'arrow'];
const cinemaFormStops = cinemaActs.map(({ point }) => point);
const cinemaCameraYaw = { cloud: -.28, orbit: .72, bulb: .24, arrow: .04 };

const formCloud = ({ a, b, c }) => {
  const theta = a * Math.PI * 2;
  const phi = Math.acos(1 - b * 2);
  const lobe = .7 + c * .29 + Math.sin(theta * 3 + phi * 1.7) * .075;
  const radial = Math.sin(phi) * lobe;
  const split = Math.cos(theta) < 0 ? -.14 : .14;
  return {
    x: Math.cos(theta) * radial * 1.18 + split,
    y: Math.cos(phi) * lobe * .78,
    z: Math.sin(theta) * radial * .68,
  };
};

const formOrbit = ({ a, b, c }) => {
  const band = Math.min(2, Math.floor(a * 3));
  const angle = b * Math.PI * 2;
  const radius = .82 + (c - .5) * .12;
  const depth = (c - .5) * .22;
  if (band === 0) return { x: Math.cos(angle) * radius * 1.1, y: Math.sin(angle) * radius * .54, z: depth };
  if (band === 1) return { x: Math.cos(angle) * radius * .76, y: depth, z: Math.sin(angle) * radius * .76 };
  return { x: Math.cos(angle) * radius * .6, y: Math.sin(angle) * radius * .72, z: Math.sin(angle) * radius * .53 + depth };
};

const formBulb = ({ a, b, c }) => {
  const angle = b * Math.PI * 2;
  if (a < .72) {
    const t = a / .72;
    const radius = .07 + Math.sin(t * Math.PI * .92) * .7;
    return { x: Math.cos(angle) * radius, y: .62 - t * 1.28, z: Math.sin(angle) * radius * .62 };
  }
  if (a < .9) {
    const t = (a - .72) / .18;
    const radius = .25 - t * .06;
    return { x: Math.cos(angle) * radius, y: -.66 - t * .24, z: Math.sin(angle) * radius * .58 };
  }
  const row = (a - .9) / .1;
  return { x: (b - .5) * .54, y: -.91 - row * .16, z: (c - .5) * .22 };
};

const formArrow = ({ a, b, c }) => {
  if (a < .36) return { x: (b - .5) * .3, y: .74 - c * 1.34, z: (b - .5) * .28 };
  const root = Math.sqrt(b);
  const point = { x: 0, y: -1.08 };
  const left = { x: -.7, y: .08 };
  const right = { x: .7, y: .08 };
  return {
    x: (1 - root) * point.x + root * ((1 - c) * left.x + c * right.x),
    y: (1 - root) * point.y + root * ((1 - c) * left.y + c * right.y),
    z: (a - .68) * .42,
  };
};

const cinemaParticles = Array.from({ length: MORPH_PARTICLE_COUNT }, (_, index) => {
  const a = seededSignal(index, 1);
  const b = seededSignal(index, 2);
  const c = seededSignal(index, 3);
  return {
    id: index,
    a,
    b,
    c,
    ambient: index % 11 === 0,
    phase: seededSignal(index, 4) * Math.PI * 2,
    size: .5 + seededSignal(index, 5) * 1.55,
    colorIndex: Math.min(particlePalette.length - 1, Math.floor(seededSignal(index, 6) * particlePalette.length)),
    scatter: {
      x: (seededSignal(index, 7) * 2 - 1) * 1.72,
      y: (seededSignal(index, 8) * 2 - 1) * 1.18,
      z: (seededSignal(index, 9) * 2 - 1) * 1.34,
    },
    forms: {
      cloud: formCloud({ a, b, c }),
      orbit: formOrbit({ a, b, c }),
      bulb: formBulb({ a, b, c }),
      arrow: formArrow({ a, b, c }),
    },
  };
});

const morphScene = (progress) => {
  const value = constrain(progress);
  const index = cinemaFormStops.reduce((lastIndex, stop, currentIndex) => (value >= stop ? currentIndex : lastIndex), 0);
  if (index === cinemaForms.length - 1) return { from: cinemaForms[index], to: cinemaForms[index], mix: 0, dissolve: 0 };
  const start = cinemaFormStops[index];
  const end = cinemaFormStops[index + 1];
  const mix = soften(constrain((value - start) / (end - start)));
  return {
    from: cinemaForms[index],
    to: cinemaForms[index + 1],
    mix,
    dissolve: Math.sin(mix * Math.PI) ** 2,
  };
};

function useCinematicProgress(stageRef, reducedMotion) {
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    if (reducedMotion) {
      setProgress(1);
      return undefined;
    }
    let frame = 0;
    let lastProgress = -1;
    const update = () => {
      frame = 0;
      const stage = stageRef.current;
      if (!stage) return;
      const rect = stage.getBoundingClientRect();
      const range = Math.max(1, stage.offsetHeight - window.innerHeight);
      const next = constrain(-rect.top / range);
      if (Math.abs(next - lastProgress) > .0015) {
        lastProgress = next;
        setProgress(next);
      }
    };
    const schedule = () => { if (!frame) frame = window.requestAnimationFrame(update); };
    schedule();
    window.addEventListener('scroll', schedule, { passive: true });
    window.addEventListener('resize', schedule);
    return () => {
      window.removeEventListener('scroll', schedule);
      window.removeEventListener('resize', schedule);
      if (frame) window.cancelAnimationFrame(frame);
    };
  }, [stageRef, reducedMotion]);

  return progress;
}

function CinematicSignalField({ progress, reducedMotion }) {
  const hostRef = useRef(null);
  const canvasRef = useRef(null);
  const progressRef = useRef(progress);
  const scene = morphScene(reducedMotion ? 1 : progress);

  useEffect(() => { progressRef.current = progress; }, [progress]);

  useEffect(() => {
    const host = hostRef.current;
    const canvas = canvasRef.current;
    const context = canvas?.getContext('2d', { alpha: true });
    if (!host || !canvas || !context) return undefined;

    let width = 0;
    let height = 0;
    let frame = 0;
    let isIntersecting = true;
    let deviceRatio = 1;
    let lastDraw = 0;
    const pointer = { x: 0, y: 0 };
    const lowPower = Number(navigator.deviceMemory || 8) < 4 || Number(navigator.hardwareConcurrency || 8) < 4;

    const rotate = (particle, yaw, pitch) => {
      const cosineYaw = Math.cos(yaw);
      const sineYaw = Math.sin(yaw);
      const x = particle.x * cosineYaw - particle.z * sineYaw;
      const z = particle.x * sineYaw + particle.z * cosineYaw;
      const cosinePitch = Math.cos(pitch);
      const sinePitch = Math.sin(pitch);
      return {
        x,
        y: particle.y * cosinePitch - z * sinePitch,
        z: particle.y * sinePitch + z * cosinePitch,
      };
    };

    const appendShard = (point) => {
      const { x, y, size, turn } = point;
      const top = turn - Math.PI / 2;
      const right = turn + Math.PI / 6;
      const left = turn + Math.PI * 5 / 6;
      context.moveTo(x + Math.cos(top) * size * 1.32, y + Math.sin(top) * size * 1.32);
      context.lineTo(x + Math.cos(right) * size, y + Math.sin(right) * size);
      context.lineTo(x + Math.cos(left) * size, y + Math.sin(left) * size);
      context.closePath();
    };

    const draw = (time = 0) => {
      if (!width || !height) return;
      context.clearRect(0, 0, width, height);
      const sceneProgress = reducedMotion ? 1 : progressRef.current;
      const morph = morphScene(sceneProgress);
      const compactViewport = width < 760;
      const activeParticles = cinemaParticles.slice(0, lowPower ? 720 : compactViewport ? 1800 : cinemaParticles.length);
      const yaw = cinemaCameraYaw[morph.from] + (cinemaCameraYaw[morph.to] - cinemaCameraYaw[morph.from]) * morph.mix + time * .000072 + pointer.x * .22;
      const pitch = -.08 + Math.sin(time * .000055) * .07 + pointer.y * .13;
      const distance = 4.56 - sceneProgress * .42;
      const scale = Math.min(width, height) / (compactViewport ? 390 : 540);
      const centerX = width * (compactViewport ? .65 : .665);
      const centerY = height * (compactViewport ? .43 : .48);
      const batches = particlePalette.map(() => [[], [], []]);

      activeParticles.forEach((particle) => {
        let positioned;
        if (particle.ambient) {
          const drift = reducedMotion ? 0 : Math.sin(time * .00014 + particle.phase) * .04;
          positioned = { x: particle.scatter.x * 1.34 + drift, y: particle.scatter.y * 1.22, z: particle.scatter.z };
        } else {
          const from = particle.forms[morph.from];
          const to = particle.forms[morph.to];
          const drift = reducedMotion ? 0 : Math.sin(time * .00021 + particle.phase) * .018;
          positioned = {
            x: from.x + (to.x - from.x) * morph.mix + particle.scatter.x * morph.dissolve * .58 + drift,
            y: from.y + (to.y - from.y) * morph.mix + particle.scatter.y * morph.dissolve * .42 + Math.cos(time * .00017 + particle.phase) * (reducedMotion ? 0 : .014),
            z: from.z + (to.z - from.z) * morph.mix + particle.scatter.z * morph.dissolve * .5,
          };
        }
        const rotated = rotate(positioned, yaw, pitch);
        const perspective = distance / (distance - rotated.z);
        const depth = constrain((rotated.z + 1.8) / 3.6);
        const alpha = particle.ambient ? .14 + depth * .2 : compactViewport ? .42 + depth * .78 : .24 + depth * .72;
        const alphaBucket = alpha > .68 ? 2 : alpha > .41 ? 1 : 0;
        batches[particle.colorIndex][alphaBucket].push({
          x: centerX + rotated.x * width * .31 * perspective,
          y: centerY + rotated.y * height * .46 * perspective,
          size: Math.max(.48, particle.size * (1.05 + depth * 1.9) * scale * perspective),
          turn: particle.phase + time * .00025 + rotated.z * .42,
        });
      });

      const halo = context.createRadialGradient(centerX, centerY, 0, centerX, centerY, Math.max(width, height) * .54);
      halo.addColorStop(0, `rgba(51, 92, 72, ${.075 + (1 - morph.dissolve) * .13})`);
      halo.addColorStop(.5, 'rgba(17, 45, 33, .025)');
      halo.addColorStop(1, 'rgba(7, 16, 13, 0)');
      context.fillStyle = halo;
      context.fillRect(0, 0, width, height);

      context.save();
      context.globalCompositeOperation = 'lighter';
      const alphaLevels = compactViewport ? [.38, .7, 1] : [.27, .56, .92];
      particlePalette.forEach((color, colorIndex) => {
        batches[colorIndex].forEach((bucket, alphaIndex) => {
          if (!bucket.length) return;
          context.beginPath();
          bucket.forEach(appendShard);
          context.globalAlpha = alphaLevels[alphaIndex];
          context.fillStyle = `rgb(${color})`;
          context.fill();
        });
      });
      context.restore();
    };

    const stop = () => {
      if (!frame) return;
      window.cancelAnimationFrame(frame);
      frame = 0;
    };

    const render = (time) => {
      frame = 0;
      if (lowPower && time - lastDraw < 32) {
        frame = window.requestAnimationFrame(render);
        return;
      }
      lastDraw = time;
      draw(time);
      if (isIntersecting && !document.hidden && !reducedMotion) frame = window.requestAnimationFrame(render);
    };

    const start = () => {
      if (!isIntersecting || document.hidden) return;
      if (reducedMotion) {
        draw(0);
        return;
      }
      if (!frame) frame = window.requestAnimationFrame(render);
    };

    const resize = () => {
      const bounds = canvas.getBoundingClientRect();
      width = Math.max(1, Math.round(bounds.width));
      height = Math.max(1, Math.round(bounds.height));
      deviceRatio = Math.min(window.devicePixelRatio || 1, lowPower ? 1.2 : 1.5);
      canvas.width = Math.round(width * deviceRatio);
      canvas.height = Math.round(height * deviceRatio);
      context.setTransform(deviceRatio, 0, 0, deviceRatio, 0, 0);
      draw(0);
    };

    const onPointerMove = (event) => {
      if (reducedMotion) return;
      const bounds = host.getBoundingClientRect();
      pointer.x = ((event.clientX - bounds.left) / bounds.width - .5) * 2;
      pointer.y = ((event.clientY - bounds.top) / bounds.height - .5) * 2;
    };
    const resetPointer = () => { pointer.x = 0; pointer.y = 0; };
    const onVisibilityChange = () => { if (document.hidden) stop(); else start(); };
    const intersectionObserver = 'IntersectionObserver' in window ? new IntersectionObserver(([entry]) => {
      isIntersecting = entry.isIntersecting;
      if (isIntersecting) start(); else stop();
    }, { threshold: .12 }) : null;
    const resizeObserver = 'ResizeObserver' in window ? new ResizeObserver(resize) : null;

    resize();
    intersectionObserver?.observe(host);
    resizeObserver?.observe(canvas);
    if (!resizeObserver) window.addEventListener('resize', resize);
    host.addEventListener('pointermove', onPointerMove);
    host.addEventListener('pointerleave', resetPointer);
    document.addEventListener('visibilitychange', onVisibilityChange);
    start();

    return () => {
      stop();
      intersectionObserver?.disconnect();
      resizeObserver?.disconnect();
      if (!resizeObserver) window.removeEventListener('resize', resize);
      host.removeEventListener('pointermove', onPointerMove);
      host.removeEventListener('pointerleave', resetPointer);
      document.removeEventListener('visibilitychange', onVisibilityChange);
    };
  }, [reducedMotion]);

  return <div ref={hostRef} className="business-cinema__field" data-particle-count={MORPH_PARTICLE_COUNT} data-morph-scene={scene.mix < .5 ? scene.from : scene.to} role="img" aria-label="Campo tridimensional com 2.400 partículas. Pelo scroll, elas formam uma nuvem, órbitas, uma lâmpada e uma seta de direção.">
    <canvas ref={canvasRef} className="business-cinema__canvas" aria-hidden="true" />
  </div>;
}

function BusinessOfferLadder() {
  return <section className="business-offers" aria-labelledby="business-offers-title">
    <div className="business-offers__lead"><h2 id="business-offers-title">A descoberta ganha a profundidade que a próxima decisão pede.</h2><p>Os três caminhos públicos aumentam a resolução da investigação. O acompanhamento mais próximo acontece por aplicação.</p></div>
    <div className="business-offers__list">{publicOffers.map((offer) => <article className="business-offer" key={offer.price}><strong>{offer.price}</strong><div><h3>{offer.name}</h3><p>{offer.copy}</p></div><a href={checkoutHref}>{offer.cta}<Arrow /></a></article>)}</div>
  </section>;
}

function BusinessPage() {
  const stageRef = useRef(null);
  const reducedMotion = useReducedMotionPreference();
  const progress = useCinematicProgress(stageRef, reducedMotion);
  const activeActIndex = cinemaActs.reduce((nearestIndex, act, index) => (
    Math.abs(progress - act.point) < Math.abs(progress - cinemaActs[nearestIndex].point) ? index : nearestIndex
  ), 0);

  return <main className="page page--business">
    <Header variant="dark" publicSurface brandHref="/" />
    <section ref={stageRef} className="business-cinema" aria-labelledby="business-title">
      <div className="business-cinema__sticky">
        <CinematicSignalField progress={progress} reducedMotion={reducedMotion} />
        <div className="business-cinema__veil" aria-hidden="true" />
        <div className="business-cinema__narrative">
          {cinemaActs.map((act, index) => {
            const isDominant = reducedMotion || index === activeActIndex;
            const opacity = isDominant ? 1 : 0;
            const offset = reducedMotion || isDominant ? 0 : (index < activeActIndex ? -24 : 24);
            const Heading = index === 0 ? 'h1' : 'h2';
            return <article className={`business-cinema__act ${isDominant ? 'is-dominant' : ''}`} key={act.point} style={{ '--cinema-opacity': opacity, '--cinema-offset': `${offset}px` }}>
              <Heading id={index === 0 ? 'business-title' : undefined}>{act.title}</Heading>
              <p>{act.copy}</p>
              {act.cta ? <CTA href={startHref} tone="brass">{act.cta}</CTA> : null}
            </article>;
          })}
        </div>
        <div className="business-cinema__progress" aria-hidden="true"><span style={{ transform: `scaleX(${Math.max(.025, progress)})` }} /></div>
        <p className="business-cinema__cue" aria-hidden="true">Desça para atravessar o campo.</p>
      </div>
    </section>
    <section className="business-bridge" aria-labelledby="business-bridge-title"><div><h2 id="business-bridge-title">Uma hipótese não elimina a incerteza.</h2><p>Ela torna a próxima conversa mais específica: o que investigar, quem ouvir e qual teste realmente faz sentido.</p></div><a className="inline-link inline-link--brass" href={startHref}>Começar pelo mapa da oportunidade <Arrow /></a></section>
    <BusinessOfferLadder />
    <section className="business-application"><div><h2>Alguns projetos avançam por aplicação.</h2><p>Se a oportunidade exigir maior participação humana, análise de contexto ou execução acompanhada, o próximo passo é uma aplicação.</p></div><CTA href={applyHref} tone="brass">Aplicar para acompanhamento</CTA></section>
    <Footer variant="dark" />
  </main>;
}

const journeyChapters = [
  ['01', 'O sinal aparece', 'Uma oportunidade começa como fragmento.'],
  ['02', 'A pergunta muda o mapa', 'A história deixa de ser contexto e passa a ser evidência.'],
  ['03', 'As conexões ganham forma', 'Capacidades, acessos e problemas se aproximam.'],
  ['04', 'A hipótese é pressionada', 'O que parece fascinante precisa encontrar a realidade.'],
  ['05', 'Uma direção se destaca', 'Poucas possibilidades chegam com razão suficiente para continuar.'],
  ['06', 'A decisão começa', 'Você escolhe até onde levar a descoberta.'],
];

function useScrollVideo(stageRef, videoRef, reducedMotion) {
  const [progress, setProgress] = useState(0);
  useEffect(() => {
    if (reducedMotion) return undefined;
    let frame = 0;
    const update = () => {
      frame = 0;
      const stage = stageRef.current;
      const video = videoRef.current;
      if (!stage) return;
      const rect = stage.getBoundingClientRect();
      const range = Math.max(1, stage.offsetHeight - window.innerHeight);
      const next = Math.min(1, Math.max(0, -rect.top / range));
      setProgress(next);
      if (video?.readyState >= 1 && Number.isFinite(video.duration) && video.duration > 0) {
        const target = Math.min(video.duration - 0.05, Math.max(0, next * video.duration));
        if (Math.abs(video.currentTime - target) > 0.035) video.currentTime = target;
      }
    };
    const schedule = () => { if (!frame) frame = window.requestAnimationFrame(update); };
    schedule();
    window.addEventListener('scroll', schedule, { passive: true });
    window.addEventListener('resize', schedule);
    videoRef.current?.addEventListener('loadedmetadata', schedule);
    return () => { window.removeEventListener('scroll', schedule); window.removeEventListener('resize', schedule); videoRef.current?.removeEventListener('loadedmetadata', schedule); if (frame) window.cancelAnimationFrame(frame); };
  }, [stageRef, videoRef, reducedMotion]);
  return progress;
}

function JourneyPage() {
  const stageRef = useRef(null);
  const videoRef = useRef(null);
  const [reducedMotion, setReducedMotion] = useState(() => window.matchMedia('(prefers-reduced-motion: reduce)').matches);
  const progress = useScrollVideo(stageRef, videoRef, reducedMotion);
  const active = Math.min(journeyChapters.length - 1, Math.floor(progress * journeyChapters.length));
  useEffect(() => {
    const media = window.matchMedia('(prefers-reduced-motion: reduce)');
    const change = (event) => setReducedMotion(event.matches);
    media.addEventListener?.('change', change);
    return () => media.removeEventListener?.('change', change);
  }, []);
  return <main className="page page--journey">
    <Header variant="dark" label="Variação 03 / Jornada" />
    <section className="scrub-stage" ref={stageRef} aria-labelledby="scrub-title">
      <div className="scrub-sticky"><div className="scrub-media" aria-hidden="true"><video ref={videoRef} poster={journeyPosterSrc || undefined} muted playsInline preload={reducedMotion || !journeyVideoSrc ? 'none' : 'metadata'}>{!reducedMotion && journeyVideoSrc ? <source src={journeyVideoSrc} type="video/mp4" /> : null}</video><span /></div><div className="scrub-copy"><p className="section-kicker">Uma jornada para quem ainda não tem uma ideia</p><h1 id="scrub-title">Você não precisa começar com uma ideia.</h1><p>Comece entendendo o que já existe em você e ao seu redor.</p><CTA href={startHref}>Começar a investigação</CTA></div><div className="scrub-progress"><span style={{ transform: `scaleX(${progress})` }} /></div><div className="scrub-chapters" aria-live="polite">{journeyChapters.map(([number, title, copy], index) => <article className={index === active ? 'is-active' : ''} key={number}><span>{number}</span><div><h2>{title}</h2><p>{copy}</p></div></article>)}</div><span className="scrub-instruction">{reducedMotion ? 'Movimento reduzido: leitura completa disponível' : journeyVideoSrc ? 'Role para atravessar a jornada' : 'Vídeo da jornada aguardando integração'}</span></div>
    </section>
    <section className="journey-after"><p className="section-kicker">O que acontece depois do percurso</p><h2>As perguntas organizam aquilo que ainda estava sem nome.</h2><div className="journey-after__grid"><p>A INÉDITA cruza sua história, capacidades, acessos, recursos, preferências e restrições.</p><p>As possibilidades são comparadas, questionadas e confrontadas com a realidade antes de uma recomendação aparecer.</p><p>O resultado explica por que aquela oportunidade faz sentido para você e quais caminhos podem seguir.</p></div><CTA href={startHref} tone="light">Descobrir minhas oportunidades</CTA></section>
    <PublicLadder tone="dark" />
    <section className="application-bridge"><div><p className="section-kicker">Depois da descoberta</p><h2>Se o próximo passo pedir companhia, existe uma aplicação.</h2><p>O acompanhamento mais próximo depende da análise do projeto e da disponibilidade.</p></div><CTA href={applyHref} tone="light">Aplicar para acompanhamento</CTA></section>
    <Footer variant="dark" />
  </main>;
}

function LabIndex() {
  const variants = [
    { href: '/biomorphic', label: '01 / Biomorphic', title: 'A investigação como objeto editorial.', copy: 'Paleta mineral, imagens BIOMORPHIC e uma leitura sobre o valor latente.', tone: 'bio' },
    { href: '/business', label: '02 / Empresas', title: 'Oportunidades para negócios que precisam decidir.', copy: 'Uma superfície empresarial para capacidades internas, ativos e problemas reais.', tone: 'business' },
    { href: '/journey', label: '03 / Jornada', title: 'A ideia aparece enquanto você atravessa.', copy: 'Uma jornada scroll scrub com vídeo de fundo e capítulos progressivos.', tone: 'journey' },
  ];
  return <main className="lab-index"><Header variant="dark" label="Subprojeto INEDITA" /><section className="lab-index__hero"><p className="section-kicker">inedita-landing-lab / três superfícies</p><h1>O mesmo método.<br /><em>Três formas de entrar.</em></h1><p>Variações para testar a primeira impressão sem perder a verdade do produto: descobrir, estruturar e decidir.</p></section><section className="variant-grid" aria-label="Variações disponíveis">{variants.map((variant) => <a className={`variant-tile variant-tile--${variant.tone}`} href={variant.href} key={variant.href}><span>{variant.label}</span><h2>{variant.title}</h2><p>{variant.copy}</p><b>Ver variação <Arrow /></b></a>)}</section><Footer variant="dark" /></main>;
}

function App() {
  const path = window.location.pathname.replace(/\/$/, '') || '/';
  const standaloneEntry = String(import.meta.env.VITE_INEDITA_ROOT_ENTRY || '').trim();
  const standalonePages = {
    biomorphic: <BiomorphicPage />,
    business: <BusinessPage />,
    journey: <JourneyPage />,
  };

  if (path === '/' && standalonePages[standaloneEntry]) return standalonePages[standaloneEntry];
  if (path === '/biomorphic') return <BiomorphicPage />;
  if (path === '/business') return <BusinessPage />;
  if (path === '/journey') return <JourneyPage />;
  return <LabIndex />;
}

createRoot(document.getElementById('root')).render(<App />);
